import { StatusCodes } from 'http-status-codes';
import React from 'react';
import GlobalModalStore from '../store/GlobalModalStore';
import { BaseDataPointResponse } from '../types/common-types';

export const AgentIASError = (): React.JSX.Element => (
  <>
    <p>We were unable to retrieve data from Cornerstone.</p>
    <p>Please make sure all Cornerstone Database Services are running and then try again.</p>
  </>
);

export const AgentIASAuthenticateError = (): React.JSX.Element => (
  <>
    <p>
      Additional backend setup is in progress for your practice and must be completed before you can
      proceed. Please try again in 1 business day.
    </p>
  </>
);

export const AgentDataPointError = (): React.JSX.Element => (
  <>
    <p>We were unable to connect to the DataPoint agent on your Cornerstone server.</p>
    <p>
      Please contact Cornerstone Support at <strong>1-800-695-2877</strong> for assistance.
    </p>
  </>
);

export const AgentCSServerError = (): React.JSX.Element => (
  <>
    <p>We were unable to retrieve information from your Cornerstone server.</p>
    <p>
      Please contact Cornerstone Support at <strong>1-800-695-2877</strong> for assistance.
    </p>
  </>
);

export const DataPointResponseCodes = {
  IAS_GENERIC_ERROR: 6,
  IAS_AUTH_ERROR: 7,
  AGENT_ERROR: 1,
  CS_SERVER_ERROR: -1, // -1 is an artificial status code for enrolled sites with DataPoint / IAS errors
} as const;

export const backendCallWithDataPointErrorsHandling = async <T,>(
  globalModalStore: GlobalModalStore,
  callFn: () => Promise<BaseDataPointResponse<T>>,
  successFn: (context: T) => void,
  errorFn?: (response: BaseDataPointResponse<T> | undefined) => void,
): Promise<void> => {
  let response: BaseDataPointResponse<T> | undefined = undefined;
  let errorMessage: any = 'An unexpected error occurred, please try again.';
  let errorHeader = 'Connection Error';
  try {
    response = await callFn();
    if (response.responseCode === DataPointResponseCodes.IAS_GENERIC_ERROR)
      errorMessage = React.createElement(AgentIASError);
    else if (response.responseCode === DataPointResponseCodes.IAS_AUTH_ERROR) {
      errorHeader = 'Additional Setup Pending';
      errorMessage = React.createElement(AgentIASAuthenticateError);
    } else if (response.responseCode === DataPointResponseCodes.AGENT_ERROR)
      errorMessage = React.createElement(AgentDataPointError);
    else if (response.responseCode === DataPointResponseCodes.CS_SERVER_ERROR)
      errorMessage = React.createElement(AgentCSServerError);
    else {
      if (response.iasHttpStatus === StatusCodes.BAD_REQUEST.valueOf()) {
        errorHeader = 'Unsupported Cornerstone Version';
        errorMessage =
          'Your current Cornerstone version does not support integration with Trupanion.';
      } else if (response.iasHttpStatus === StatusCodes.OK.valueOf()) {
        successFn(response.context);
        errorMessage = undefined;
      }
    }
  } catch (e: any) {
    if (e.message?.includes('Request failed with status code 403')) {
      errorHeader = 'Additional Setup Pending';
      errorMessage = React.createElement(AgentIASAuthenticateError);
    }
  }
  if (errorMessage) {
    globalModalStore.showOkPopup(errorHeader, errorMessage, () => errorFn?.(response));
  }
};
